'use client';

import { ChangeEvent, useContext, useState, type FC } from 'react';
import {
  Text,
  Button,
  ButtonGroup,
  Flex,
  Heading,
  Password,
  Separator,
} from '@/_components';
import type { WithPasswordFlowProps } from './WithPasswordFlow.types';
import { UserContext } from '@/_contexts/UserProvider';
import { ToastContext } from '@/_contexts/ToastProvider';
import { setSignInMethod } from '@/_api/users';
import { checkPasswordForError } from '@/_utils/passwords';

export const WithPasswordFlow: FC<WithPasswordFlowProps> = ({ onUpdate }) => {
  const { token } = useContext(UserContext);
  const { addToast } = useContext(ToastContext);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [newPassword, setNewPassword] = useState<string>('');
  const [newPasswordConfirmation, setNewPasswordConfirmation] =
    useState<string>('');

  const { isValid } = checkPasswordForError(
    newPassword,
    newPasswordConfirmation,
  );

  const onNewPasswordChange = (event: ChangeEvent<HTMLInputElement>): void =>
    setNewPassword(event.target.value);
  const onNewPasswordChange2 = (event: ChangeEvent<HTMLInputElement>): void =>
    setNewPasswordConfirmation(event.target.value);

  const onSubmit = async () => {
    if (!isValid) return;

    let isUpdated: boolean = false;

    setIsLoading(true);

    try {
      const { error } = await setSignInMethod({
        signInMethod: 'password',
        newPassword,
        token,
      });

      if (!error) {
        isUpdated = true;
        addToast('success', 'Password updated successfully');
      } else {
        addToast('error', error, { error });
      }
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      onUpdate(isUpdated, 'password');
      setIsLoading(false);
    }
  };

  const onCancel = () => onUpdate(false);

  return (
    <Flex isColumn={true} gap={8}>
      <Flex isColumn={true} gap={2}>
        <Heading level={3}>Update sign-in method</Heading>
        <Text type="small">Please enter your new password to confirm</Text>
      </Flex>
      <Separator />
      <Password
        label="New password"
        value={newPassword}
        placeholder="Password"
        onChange={onNewPasswordChange}
      />
      <Password
        label="Confirm password"
        value={newPasswordConfirmation}
        placeholder="Please confirm your password"
        confirmPassword={newPassword}
        showIndicator={true}
        onChange={onNewPasswordChange2}
      />

      <ButtonGroup align="right">
        <Button
          value="Cancel"
          intent="secondary"
          onClick={onCancel}
          isDisabled={isLoading}
        />
        <Button
          value="Set password"
          intent="primary"
          onClick={onSubmit}
          isLoading={isLoading}
          isDisabled={!isValid || isLoading}
        />
      </ButtonGroup>
    </Flex>
  );
};
